import { roundToPar } from '../../utils/golf';
import { isMatchplayFormat } from '../../utils/matchplay';

// Score presentation for the Home cards. Kept out of the component so the same
// definitions the screen renders can be pinned by scoreToPar.test.js — Home was
// previously deriving its own to-par and disagreeing with the leaderboard.

// Format a "to par" value from either:
//  • A backend-provided score_to_par ('E' or number)
//  • A pre-computed delta (number)
// Returns { txt, cls } or null when there's nothing meaningful to display.
export function fmtToPar(value) {
  if (value === undefined || value === null || value === '') return null;
  if (value === 'E') return { txt: 'E', cls: 'ph-sc-neutral' };
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(n)) return null;
  if (n === 0) return { txt: 'E', cls: 'ph-sc-neutral' };
  return {
    txt: n > 0 ? `+${n}` : `${n}`,
    cls: n < 0 ? 'ph-sc-good' : 'ph-sc-bad',
  };
}

// Compute to-par + points from a leaderboard entry (works for both
// /live_leaderboard and /leaderboard_summary shapes).
//
// score_to_par, when the server sent one, is canonical — use it untouched. Only
// the summary shape lacks it, and there the figure MUST come from the shared
// roundToPar: under Stableford to-par is points pace, (2 × holes) − points, not
// nett strokes. Deriving it as nett − par here is what made Home disagree with
// the leaderboard.
export function rowScores(entry, isStrokePlay = false) {
  const holesPlayed = entry.thru ?? (entry.rounds_played ? entry.rounds_played * 18 : 0);
  const nettStrokesToPar = entry.total_par > 0 && entry.total_nett != null
    ? entry.total_nett - entry.total_par
    : null;
  const canDerive = holesPlayed > 0
    && (isStrokePlay ? nettStrokesToPar != null : entry.total_stableford != null);
  const derived = canDerive
    ? roundToPar({
        isStrokePlay,
        holesPlayed,
        points: entry.total_stableford ?? 0,
        nettStrokesToPar,
      })
    : null;
  const tp = fmtToPar(entry.score_to_par) || fmtToPar(derived);

  let pts = '—', ptsCls = 'ph-sc-neutral';
  if (entry.total_stableford != null && entry.total_stableford > 0) {
    pts = `${entry.total_stableford}`;
    ptsCls = entry.total_stableford >= 36 ? 'ph-sc-good' : 'ph-sc-neutral';
  } else if (entry.total_gross != null && entry.total_gross > 0) {
    pts = `${entry.total_gross}`;
  }

  return {
    toPar: tp?.txt ?? '—',
    toParCls: tp?.cls ?? 'ph-sc-neutral',
    pts,
    ptsCls,
  };
}

// To-par for a `/home-feed` entry — "elsewhere in the society" and the recent
// activity row.
//
// That endpoint deliberately does NOT send a to-par figure. It sends this
// function's ARGUMENTS (thru, stableford_points, and for stroke play the nett
// strokes against the pars actually played) precisely so the number is decided
// once, here, by the same roundToPar every other surface uses. Adding a
// server-side derivation for these two cards would be a fourth definition of
// to-par, which is the drift this app keeps having to undo.
export function feedToPar(entry) {
  if (!entry || !entry.thru) return null;
  // A MATCH has no to-par. It ends when it is won and holes are conceded, so
  // "thru 15, +4" describes a card that was never played out. The server sends
  // the flag (`is_matchplay` on both feed shapes) because the format string
  // does not travel on this endpoint. The row keeps its gross and its thru —
  // both of those are still true — and simply drops the figure.
  if (entry.is_matchplay) return null;
  const isStrokePlay = !!entry.is_stroke_play;
  if (isStrokePlay && entry.nett_strokes_to_par == null) return null;
  return fmtToPar(roundToPar({
    isStrokePlay,
    holesPlayed: entry.thru,
    points: entry.stableford_points ?? 0,
    nettStrokesToPar: entry.nett_strokes_to_par,
  }));
}

// What a finished card is WORTH, as one line of the recent-activity row.
//
// The FORMAT decides, not the card. Home used to print "Will shot 70" over
// every round it was handed, and over a match that is a number describing a
// round nobody played: a match ends when it is won (4&3 leaves three holes
// unplayed) and holes are conceded rather than holed out, so its stroke total
// is arithmetic over something that did not happen. A Stableford day has the
// same problem in reverse — the gross is not what anybody was playing for.
//
// So: points for a points competition, a stroke total for stroke play, and a
// RESULT for a match. The server sends the facts (`is_matchplay`, `match`,
// `stableford_points`, `gross_score`); the sentence is composed here, once,
// where scoreToPar.test.js can pin it.
/**
 * What one round of the society's week is worth, in a sentence.
 *
 * The ROW IS A ROUND now, not a player: it used to dedupe by player, so two of
 * the same fourball both qualified and the list read as one round announced
 * twice. The name in the sentence is the card that won it, and `field_size`
 * says how many were out — "Will 38 pts and 11 others" tells a reader there
 * was a society day on, where "Will 38 pts" tells them about Will.
 */
export function activityLine(entry) {
  if (!entry) return null;
  const who = entry.is_you ? 'You' : (entry.first_name || 'Someone');
  // "and 3 others" — everyone in the round but the one named.
  const others = Math.max(0, (entry.field_size || 1) - 1);
  const withThem = others ? ` and ${others} other${others === 1 ? '' : 's'}` : '';

  if (entry.is_matchplay) {
    const m = entry.match;
    // No draw behind it, or a match that never reached a verdict — say what is
    // true (they played one) rather than inventing a winner.
    if (!m || (m.outcome !== 'won' && m.outcome !== 'lost' && m.outcome !== 'halved')) {
      return `${who} played a match`;
    }
    if (m.outcome === 'halved') return `${who} halved a match`;
    return `${who} ${m.outcome} ${m.score}`;
  }

  if (!entry.is_stroke_play && entry.stableford_points) {
    return `${who} ${entry.stableford_points} pts${withThem}`;
  }
  if (entry.gross_score != null) return `${who} shot ${entry.gross_score}${withThem}`;
  return `${who} played${withThem}`;
}

// ── Recent form ────────────────────────────────────────────────────────────
//
// The tiles under "RECENT FORM" — the last five rounds — and the one place
// that decides what a finished round is WORTH.
//
// It used to read `stableford_total` off every round whatever game was played,
// so a Stroke Play round appeared as "36 points" — an arithmetically real
// number (the engine scores Stableford points on every hole regardless of
// format) for a competition that player was never in. Worse, the tiles
// were then averaged and a best picked across them, comparing a Stableford 38
// against a Stroke Play card as though they were the same measurement.
//
// Score TO PAR is the one figure the two formats share, and `roundToPar` is
// already the app's single definition of it: points pace under Stableford,
// nett strokes under stroke play. So the tiles show to-par, they are
// comparable, and — usefully — level par IS playing to handicap in both
// formats, which is what the old "vs hcp" line was trying to say.
//
// A round whose format or card is unknown is DROPPED, never guessed at. That
// is the whole bug this replaces.
export function formToPar(round) {
  if (!round) return null;
  const holes = round.total_holes;
  if (!holes) return null;
  // A round still being played is not form yet. It is not "skipped" either —
  // see `isFormCard`, which is the question `finalized=1` used to be asked in
  // its place.
  if (!isFormCard(round)) return null;
  // A MATCHPLAY round is dropped for the same reason a formatless one is: the
  // number cannot be read. A match ends when it is won, so its card is short by
  // design — 4&3 leaves three holes unplayed, worth nought points each — and
  // conceded holes carry no score either. Rating that against a full Stableford
  // round would put a good win on the tiles as the worst golf of the month.
  //
  // Dropped, not zeroed: `recentForm` counts what it could not read, so the
  // panel says "4 of 5" rather than quietly averaging four rounds and calling
  // them five.
  if (isMatchplayFormat(round.tournament_format)) return null;
  const isStrokePlay = round.tournament_format === 'Stroke Play';
  if (isStrokePlay) {
    if (round.total_par == null || round.nett_score_total == null) return null;
    return roundToPar({
      isStrokePlay: true,
      holesPlayed: holes,
      nettStrokesToPar: round.nett_score_total - round.total_par,
    });
  }
  if (round.tournament_format == null || round.stableford_total == null) return null;
  return roundToPar({
    isStrokePlay: false,
    holesPlayed: holes,
    points: round.stableford_total,
  });
}

/**
 * Handicap movement across the recent window, for the arrow beside HCP in the
 * hero strip. Returns null when there is nothing honest to show.
 *
 * `rounds` arrive NEWEST FIRST. `currentIndex` is the player's live handicap —
 * the number the arrow sits next to.
 *
 * The end point MUST be `currentIndex`, not a round's stored index.
 * `player_handicap_index` is the index a round was STARTED on: the snapshot
 * the server adds the adjustment to when the round is finalised
 * (`scoring.py::end_round`). So the newest round's value is the index from
 * BEFORE that round's cut — the very change the arrow exists to report.
 * Measuring newest-snapshot against oldest-snapshot left the arrow a round
 * behind: shoot 42 points, get cut 2.0, and the HCP beside it dropped to 9.4
 * while the arrow did not move until the round after.
 *
 * One finalised round is enough for a reading: it has a before (its snapshot)
 * and an after (the live index).
 */
export function handicapTrend(rounds, currentIndex) {
  if (currentIndex == null || Number.isNaN(Number(currentIndex))) return null;
  const starts = (rounds || [])
    .map(r => r.player_handicap_index)
    .filter(v => v != null && !Number.isNaN(Number(v)));
  if (!starts.length) return null;

  const oldest = Number(starts[starts.length - 1]);
  const diff = +(Number(currentIndex) - oldest).toFixed(1);
  if (diff === 0) return null;

  const n = starts.length;
  const over = `over your last ${n} round${n === 1 ? '' : 's'}`;
  return diff < 0
    ? { arrow: '↓', label: `${Math.abs(diff)}`, down: true, rounds: n, title: `Down ${Math.abs(diff)} ${over}` }
    : { arrow: '↑', label: `${diff}`, down: false, rounds: n, title: `Up ${diff} ${over}` };
}

/**
 * The formats recent form is ABOUT: an individual card, played for a total.
 *
 * Stableford and Stroke Play are the two the wizard writes for an individual
 * competition (`Step3_Scoring`), and they are the two whose gross total means
 * what it says. Everything else is excluded by NOT being on this list rather
 * than by being recognised and rejected: matchplay (a card that was never
 * completed), a round with no tournament behind it at all, and any future
 * format nobody has taught this panel to read. A round wrongly included prints
 * a number that means nothing, which is the bug this list exists to prevent.
 *
 * A team format layered over a Stableford tournament is fine and belongs here:
 * the player played their own ball and their own card. The shared-ball ones —
 * Foursomes, Greensomes, Scramble — never reach this list at all, because
 * their scores live on a `TeamRound` and there is no individual round to read.
 */
const FORM_FORMATS = ['stableford', 'stroke play'];

export const isFormFormat = (round) =>
  FORM_FORMATS.includes((round?.tournament_format || '').trim().toLowerCase());

/**
 * The figure on a form tile: the round's GROSS score.
 *
 * Gross, because it is the one number that means the same thing in both
 * formats and needs nothing derived to read it — the shots it took. To-par was
 * the honest way to compare a Stableford round with a Stroke Play one, and it
 * was also a figure you had to think about; the gross total is the one a
 * golfer says out loud.
 *
 * EIGHTEEN HOLES ONLY, which is the one thing gross cannot absorb: 43 sitting
 * in a row beside 86 reads as the round of a lifetime rather than as a nine.
 * Such a card is passed over and counted, like any other round the panel
 * cannot put on this scale.
 */
export function formGross(round) {
  if (!isFormCard(round) || !isFormFormat(round)) return null;
  if (round.total_holes !== 18) return null;
  const gross = round.gross_score_total;
  return typeof gross === 'number' && gross > 0 ? gross : null;
}

/**
 * Is this round a CARD — played to the end of the eighteen (or the nine)?
 *
 * NOT "was it finalised". Finalising is the ORGANISER's act: it applies the
 * handicap revision and closes the competition, and a society that never gets
 * round to it left its members' own golf out of their own form panel. Home
 * asked `/rounds?finalized=1` and was the only screen in the app that did — My
 * Performance counts every round — so the two disagreed about how much golf a
 * player had played.
 *
 * What form actually needs is a COMPLETE card: every hole on it has a score,
 * so the totals mean what they say. A round being scored this afternoon is
 * left out by the same test.
 *
 * `holes_scored` comes down with the summary shape. A payload from before it
 * existed — a localStorage snapshot, or a shipped Capacitor build talking to a
 * newer server — falls back to `is_finalized`, which was the old question and
 * is a subset of the right one: those rounds are all complete.
 */
export function isFormCard(round) {
  if (!round) return false;
  // No card to measure against (a round with no course, or a payload from
  // before `holes_scored`): fall back to the organiser's word for it. That is
  // the old question, and every round it says yes to IS complete — it is only
  // the ones it says no to that were being lost.
  if (round.holes_scored == null || !round.total_holes) return !!round.is_finalized;
  return round.holes_scored >= round.total_holes;
}

/** Five tiles is what the panel is FOR — the window a golfer reads as form. */
export const FORM_ROUNDS = 5;

/**
 * How many rounds to ask the server for to find those five.
 *
 * NOT five. `formToPar` drops a round it cannot read — a match above all, which
 * has no total worth reading — so asking for exactly five and drawing whatever
 * survived meant a player who had been playing matchplay got three tiles under
 * a heading promising five, with nothing on the page to say why. Twelve is
 * enough to find five cards through a run of matches and still cheap: these are
 * summary rows, no hole scores and no course.
 */
export const FORM_WINDOW = 12;

// The recent-form panel, or null when nothing in the window can be read.
//
// `rounds` arrive NEWEST FIRST (the order `/rounds?limit=N` returns), and the
// tiles are reversed so the strip reads left to right as the rounds were
// played — the same direction the driving strip on Your Game runs.
//
// It takes the newest `size` READABLE rounds out of however many it is handed,
// and reports what it passed over on the way (`skipped`) so the panel can say
// so. Five tiles, or a stated reason there are fewer.
export function recentForm(rounds, size = FORM_ROUNDS) {
  const all = rounds || [];
  const readable = all
    .map((r, i) => ({ round: r, gross: formGross(r), toPar: formToPar(r), at: i }))
    .filter(r => r.gross != null);
  const scored = readable.slice(0, size);
  if (!scored.length) return null;

  // How far down the list it had to go. A full set of tiles was found within
  // the first `at + 1` rounds; a short set means every round was examined.
  const examined = scored.length === size ? scored[scored.length - 1].at + 1 : all.length;
  // Only FINISHED cards count as passed over. A round in progress is not a
  // round the panel declined to read, and "1 round not counted" under the
  // tiles while you are still out on the course is a bug report waiting to
  // happen.
  const passedOver = all.slice(0, examined)
    .filter(r => isFormCard(r) && formGross(r) == null);

  const values = scored.map(r => r.gross);
  // Lower is better — the same direction to-par ran in, and the reason the
  // tiles never showed points, where higher was.
  const best = Math.min(...values);
  const avg = values.reduce((s, v) => s + v, 0) / values.length;
  // Newest first is how they arrive; [0] against [1] is the latest round
  // against the one before it. Negative is IMPROVING, as everywhere else.
  const delta = values.length >= 2 ? values[0] - values[1] : 0;

  // Which half of the card is carrying you. Points either way: more points is
  // the better nine whatever the competition is scored on, and this is an
  // internal comparison between two halves of the same rounds, never a figure
  // laid against another player's.
  const front9 = scored.reduce((s, r) => s + (r.round.stableford_front_9 ?? 0), 0);
  const back9 = scored.reduce((s, r) => s + (r.round.stableford_back_9 ?? 0), 0);
  const stronger = (!front9 && !back9) || front9 === back9
    ? null : (front9 > back9 ? 'Front 9' : 'Back 9');

  return {
    tiles: [...scored].reverse().map(r => ({
      gross: r.gross,
      toPar: r.toPar,
      best: r.gross === best,
      key: r.round.id,
    })),
    count: scored.length,
    // The rounds these tiles ARE, newest first. The handicap arrow beside HCP
    // is measured over this same list, so the two always describe one run of
    // golf — which they stopped doing the moment the fetch got wider than the
    // panel.
    window: scored.map(r => r.round),
    // Rounds passed over to find them, so the panel can say so rather than
    // quietly drawing three tiles under a heading that promises five.
    skipped: passedOver.length,
    skippedMatchplay: passedOver
      .filter(r => isMatchplayFormat(r.tournament_format)).length,
    avg: Math.round(avg * 10) / 10,
    best,
    delta,
    // Still a to-PAR question, and the only handicap-relative figure left on
    // the panel now the tiles are gross: level par IS playing to handicap, in
    // both formats. A round whose to-par cannot be read (stroke play with no
    // par on the card) is not counted as one played to handicap.
    toHandicap: scored.filter(r => r.toPar != null && r.toPar <= 0).length,
    stronger,
  };
}

// A /leaderboard_summary entry totals EVERY finalised round, but the leaderboard
// these cards link to is round-scoped — that mismatch is why Home read 142 points
// where the board said 106. Narrow a summary entry to its latest round so the two
// agree; single-round tournaments are already equivalent.
export function toLatestRound(entries) {
  return entries.map((e) => {
    const details = e.round_details || [];
    if (details.length < 2) return e;
    const latest = details.reduce((a, b) => (b.round_number > a.round_number ? b : a));
    return {
      ...e,
      total_stableford: latest.stableford_total ?? 0,
      total_nett: latest.nett_score_total ?? 0,
      total_gross: latest.gross_score_total ?? 0,
      total_par: latest.total_par ?? 0,
      rounds_played: 1,
      round_number: latest.round_number,
    };
  });
}

// Re-rank after narrowing, so the position column matches the figures beside it.
// Mirrors the server's live ordering, minus countback — this is a 3-row preview.
export function rankByRound(entries, isStrokePlay) {
  return [...entries]
    .sort((a, b) => (isStrokePlay
      ? (a.total_nett ?? 0) - (b.total_nett ?? 0)
      : (b.total_stableford ?? 0) - (a.total_stableford ?? 0))
      || (a.total_gross ?? 0) - (b.total_gross ?? 0))
    .map((e, i) => ({ ...e, position: i + 1 }));
}

/**
 * The line under the form tiles.
 *
 * It has one job beyond decoration: when there are fewer than five tiles, SAY
 * WHY. "Last 3 rounds" under a panel meant to show five reads as a bug, and it
 * was reported as one — the three were all the rounds that could be read, the
 * other two being matches, and nothing on the page said so.
 */
export function formFootnote(insights) {
  if (!insights) return 'No rounds yet';
  const n = insights.count;
  const head = `Last ${n} round${n === 1 ? '' : 's'}`;
  const skipped = insights.skipped || 0;
  if (!skipped) {
    return `${head} · ${insights.delta <= 0 ? 'trending up' : 'building form'}`;
  }
  // "Matches" only where every one of them was a match, which is the ordinary
  // case; anything else is a round whose card could not be read at all.
  const matchesOnly = insights.skippedMatchplay === skipped;
  const noun = matchesOnly
    ? `match${skipped === 1 ? '' : 'es'}`
    : `round${skipped === 1 ? '' : 's'}`;
  return `${head} · ${skipped} ${noun} not counted`;
}
